import { useState, useEffect } from 'react';

const STORAGE_KEY = 'sortify_first_time_visitor';
const TUTORIAL_COMPLETED_KEY = 'sortify_tutorial_completed';

/**
 * Custom hook to manage first-time visitor experience
 * Uses localStorage to persist visitor status across sessions
 */
export const useFirstTimeVisitor = () => {
    const [isFirstTimeVisitor, setIsFirstTimeVisitor] = useState(false);
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialStep, setTutorialStep] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const checkFirstTimeVisitor = () => {
            try {
                const hasVisited = localStorage.getItem(STORAGE_KEY);
                const tutorialCompleted = localStorage.getItem(TUTORIAL_COMPLETED_KEY);

                const isFirstTime = !hasVisited;
                const shouldShowTutorial = isFirstTime && !tutorialCompleted;

                setIsFirstTimeVisitor(isFirstTime);
                setShowTutorial(shouldShowTutorial);

                // Mark as visited on first load
                if (isFirstTime) {
                    localStorage.setItem(STORAGE_KEY, 'true');
                }

                setIsLoading(false);
            } catch (error) {
                console.error('Error checking first-time visitor status:', error);
                setIsLoading(false);
            }
        };

        checkFirstTimeVisitor();
    }, []);

    const startTutorial = () => {
        setShowTutorial(true);
        setTutorialStep(0);
    };

    const nextTutorialStep = () => {
        setTutorialStep(prev => prev + 1);
    };

    const prevTutorialStep = () => {
        setTutorialStep(prev => Math.max(0, prev - 1));
    };

    const skipTutorial = () => {
        setShowTutorial(false);
        setTutorialStep(0);
        localStorage.setItem(TUTORIAL_COMPLETED_KEY, 'true');
    };

    const completeTutorial = () => {
        setShowTutorial(false);
        setTutorialStep(0);
        setIsFirstTimeVisitor(false);
        localStorage.setItem(TUTORIAL_COMPLETED_KEY, 'true');
    };

    const resetFirstTimeVisitor = () => {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(TUTORIAL_COMPLETED_KEY);
        setIsFirstTimeVisitor(true);
        setShowTutorial(true);
        setTutorialStep(0);
    };

    return {
        isFirstTimeVisitor,
        showTutorial,
        tutorialStep,
        isLoading,
        startTutorial,
        nextTutorialStep,
        prevTutorialStep,
        skipTutorial,
        completeTutorial,
        resetFirstTimeVisitor
    };
};

export default useFirstTimeVisitor;
